import Axios from "axios";
import {
  getPrefix,
  getSession,
  getSessionToken,
  getSessionUserEntityId,
  setSession,
} from "../bandung";
import { isValidPassword, objectToFormData } from "./helpers";

const getSessionPrefix = () => `${getPrefix()}/app/session`;

const getSessionCreateUserEmailEndPoint = () =>
  `${getSessionPrefix()}/createuseremail`;
const getSessionDeleteUserEmailEndPoint = () =>
  `${getSessionPrefix()}/deleteuseremail`;
const getSessionImageEndPoint = (userEntityId) =>
  `${getSessionPrefix()}/imageimage?userEntityId=${userEntityId}&t=${
    getSession()?.version ?? Date.now()
  }`;
const getSessionJoinEndPoint = () => `${getSessionPrefix()}/join`;
const getSessionListUserEmailEndPoint = () =>
  `${getSessionPrefix()}/listuseremail?sessionToken=${getSessionToken()}`;
const getSessionSigninEndPoint = () => `${getSessionPrefix()}/signin`;
const getSessionTestEndPoint = () =>
  `${getSessionPrefix()}/test?sessionToken=${getSessionToken()}`;
const getSessionTextLongDescriptionEndPoint = () =>
  `${getSessionPrefix()}/textlongdescription?sessionToken=${getSessionToken()}`;
const getSessionTextShortDescriptionEndPoint = () =>
  `${getSessionPrefix()}/textshortdescription?sessionToken=${getSessionToken()}`;
const getSessionUpdateEndPoint = () => `${getSessionPrefix()}/update`;
const getSessionUpdateLongDescriptionEndPoint = () =>
  `${getSessionPrefix()}/updatelongdescription`;
const getSessionUpdatePasswordEndPoint = () =>
  `${getSessionPrefix()}/updatepassword`;
const getSessionUpdateShortDescriptionEndPoint = () =>
  `${getSessionPrefix()}/updateshortdescription`;
const getSessionUploadImageEndPoint = () => `${getSessionPrefix()}/uploadimage`;
const getSessionViewEndPoint = () =>
  `${getSessionPrefix()}/view?sessionToken=${getSessionToken()}`;

const sessionCreateUserEmail = async (newEmail) => {
  const emailRegex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;

  if (!emailRegex.test(newEmail)) {
    return {
      code: "INVALID_EMAIL_ERROR",
      message: "This email is invalid.",
    };
  }

  const currentEmails = (await sessionListUserEmail()).data;
  if (currentEmails.some((email) => email.email === newEmail)) {
    return {
      code: "REPEAT_EMAIL_ERROR",
      message: "This email is already on your account.",
    };
  }

  const form = objectToFormData({
    sessionToken: getSessionToken(),
    email: newEmail,
  });
  try {
    const response = await Axios.post(
      getSessionCreateUserEmailEndPoint(),
      form,
    );
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: `"${newEmail}" was added successfully.`,
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionDeleteUserEmail = async (email, emailId) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    userEmailEntityId: emailId,
  });
  try {
    const response = await Axios.post(
      getSessionDeleteUserEmailEndPoint(),
      form,
    );
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: `"${email}" was deleted successfully.`,
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionJoin = async (
  firstName,
  lastName,
  email,
  emailRepeat,
  password,
  passwordRepeat,
) => {
  if (email !== emailRepeat) {
    return {
      code: "EMAIL_MISMATCH",
      message: "Emails don't match.",
    };
  }

  if (password !== passwordRepeat) {
    return {
      code: "PASSWORD_MISMATCH",
      message: "Passwords don't match.",
    };
  }

  if (!isValidPassword(password)) {
    return {
      code: "INVALID_PASSWORD",
      message:
        "Password must be at least 8 characters long, contain at least 1 letter and 1 number, and can only contain the special characters !@#$%^&*.",
    };
  }

  const form = objectToFormData({
    firstName: firstName,
    lastName: lastName,
    email: email,
    password: password,
    passwordRepeat: passwordRepeat,
  });
  try {
    const response = await Axios.post(getSessionJoinEndPoint(), form);
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "Joined successfully.",
        data: response.data,
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionListUserEmail = async () => {
  try {
    const response = await Axios.get(getSessionListUserEmailEndPoint());
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "Email loaded successfully.",
        data: response.data,
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionSignin = async (email, password) => {
  const form = objectToFormData({
    sessionEmail: email,
    sessionPassword: password,
  });
  try {
    const response = await Axios.post(getSessionSigninEndPoint(), form);
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "Signed in successfully",
        data: response.data,
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionTest = async () => {
  try {
    const response = await Axios.get(getSessionTestEndPoint());
    if (response.data.confirm) {
      return {
        code: "SUCCESS",
        message: "Session is valid.",
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionTextLongDescription = async () => {
  try {
    const response = await Axios.get(getSessionTextLongDescriptionEndPoint());
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "Long description retrieved successfully.",
        data: decodeURIComponent(response.data),
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionTextShortDescription = async () => {
  try {
    const response = await Axios.get(getSessionTextShortDescriptionEndPoint());
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "Short description retrieved successfully.",
        data: decodeURIComponent(response.data),
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionUpdate = async ({
  firstName,
  lastName,
  email,
  phone,
  linkedinUserId,
  facebookUserId,
  gmailUserId,
  skypeUserId,
}) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    userEntityId: getSessionUserEntityId(),
    firstName: firstName,
    lastName: lastName,
    phone: phone,
    linkedinUserId: linkedinUserId,
    facebookUserId: facebookUserId,
    gmailUserId: gmailUserId,
    skypeUserId: skypeUserId,
  });
  try {
    const response = await Axios.post(getSessionUpdateEndPoint(), form);
    if (!response.data.error) {
      const prevLocalSession = JSON.parse(localStorage.getItem("bandung"));
      setSession({
        ...prevLocalSession,
        firstName,
        lastName,
        email,
        phone,
        linkedinUserId,
        facebookUserId,
        gmailUserId,
        skypeUserId,
      });
      return {
        code: "SUCCESS",
        message: "Update successful.",
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionUpdateLongDescription = async (longDescription) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    longDescription: encodeURIComponent(longDescription),
  });
  try {
    const response = await Axios.post(
      getSessionUpdateLongDescriptionEndPoint(),
      form,
    );
    if (response.data.entity) {
      return {
        code: "SUCCESS",
        message: "Long description updated successfully.",
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionUpdatePassword = async (
  currentPassword,
  newPassword,
  newPasswordRepeat,
) => {
  if (newPassword !== newPasswordRepeat) {
    return {
      code: "PASSWORD_MISMATCH",
      message: "Passwords don't match.",
    };
  }

  if (!isValidPassword(newPassword)) {
    return {
      code: "INVALID_PASSWORD",
      message:
        "Password must be at least 8 characters long, contain at least 1 letter and 1 number, and can only contain the special characters !@#$%^&*.",
    };
  }

  if (currentPassword === newPassword) {
    return {
      code: "REUSED_PASSWORD",
      message: "The new password is the same as the current password.",
    };
  }

  const form = objectToFormData({
    sessionToken: getSessionToken(),
    password: currentPassword,
    passwordNew: newPassword,
  });

  try {
    const response = await Axios.post(getSessionUpdatePasswordEndPoint(), form);
    if (response.data.confirm === "confirm") {
      return {
        code: "SUCCESS",
        message: "Congratulations, you have successfully reset your password.",
      };
    } else if (response.data.error === "Old password incorrect.") {
      return {
        code: "INCORRECT_OLD_PASSWORD",
        message: "Password is incorrect.",
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionUpdateShortDescription = async (shortDescription) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    shortDescription: encodeURIComponent(shortDescription),
  });
  try {
    const response = await Axios.post(
      getSessionUpdateShortDescriptionEndPoint(),
      form,
    );
    if (response.data.entity) {
      return {
        code: "SUCCESS",
        message: "Short description updated successfully.",
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionUploadImage = async (image, userEntityId, onUpload) => {
  const form = objectToFormData({
    sessionToken: getSessionToken(),
    userEntityId: userEntityId,
    image: image,
  });

  try {
    const response = await Axios.post(getSessionUploadImageEndPoint(), form, {
      onUploadProgress: (progressEvent) => onUpload(progressEvent),
    });
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "New profile image saved.",
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

const sessionView = async () => {
  try {
    const response = await Axios.get(getSessionViewEndPoint());
    if (!response.data.error) {
      return {
        code: "SUCCESS",
        message: "Session retrieved successfully",
        data: response.data,
      };
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    return {
      code: "ERROR",
      message: error.message || "An error occurred.",
    };
  }
};

export {
  getSessionImageEndPoint,
  sessionCreateUserEmail,
  sessionDeleteUserEmail,
  sessionJoin,
  sessionListUserEmail,
  sessionSignin,
  sessionTest,
  sessionTextLongDescription,
  sessionTextShortDescription,
  sessionUpdate,
  sessionUpdateLongDescription,
  sessionUpdatePassword,
  sessionUpdateShortDescription,
  sessionUploadImage,
  sessionView,
};
